<script setup>
import {computed, ref, watch} from 'vue';
import {Dialog, DialogPanel, DialogTitle, TransitionRoot} from '@headlessui/vue';
import {useI18n} from 'vue-i18n';
import CurrencyInput from '@/components/forms/CurrencyInput.vue';
import {formatYearsString} from '@/helper';

/**
 * Přidání / úprava strategie v kalkulačce ASO/SO (tlačítko „Přidat strategii“ a tužka na kartě).
 *
 * Nabízí jen strategie z vyhodnocení dotazníku, které v kalkulačce ještě nejsou (u úpravy i tu upravovanou).
 * V režimu plánu se zadávají částky a horizont; v režimu cíle je dopočítá kalkulačka z cílové částky
 * a rozdělení, proto modal jen informuje o alokaci a dopočtené investici.
 */
const props = defineProps({
  // upravovaná položka {strategy_id, name, initial_deposit, regular_deposit, duration}, null = nová
  item: {type: Object, default: null},
  // strategie, které lze vybrat
  strategies: {type: Array, required: true},
  // 'SAVING' (plán) / 'TARGET' (cíl)
  mode: {type: String, required: true},
  currency: {type: String, default: 'CZK'},
  // limity částek pro měnu (initial/regular_deposit_min/max)
  limits: {type: Object, default: () => ({})},
  // výchozí hodnoty nové strategie (společné částky, výchozí horizont)
  defaults: {type: Object, default: () => ({})},
  // režim cíle: text s alokací a dopočtenou investicí upravované strategie
  targetInfo: {type: String, default: null},
});
// confirm(item) – uložená položka, cancel – zavření bez změny
const emit = defineEmits(['confirm', 'cancel']);

const {t} = useI18n();
const isOpen = ref(true);
const isEdit = props.item !== null;

const firstStrategy = props.strategies[0] ?? null;
const form = ref(isEdit ? {...props.item} : {
  strategy_id: firstStrategy?.id ?? null,
  name: firstStrategy?.strategy_name ?? firstStrategy?.name ?? '',
  initial_deposit: props.defaults.initial_deposit ?? 0,
  regular_deposit: props.defaults.regular_deposit ?? 0,
  duration: props.defaults.duration ?? 30,
  alloc_pct: null,
});

const strategyName = id => {
  const strategy = props.strategies.find(s => s.id === Number(id));
  return strategy?.strategy_name ?? strategy?.name ?? '';
};
const selectedStrategy = computed(() => props.strategies.find(s => s.id === Number(form.value.strategy_id)) ?? null);

/** Při změně strategie se přepíše i název – jen pokud ho uživatel neměnil (je to název předchozí strategie). */
watch(() => form.value.strategy_id, (newId, previousId) => {
  if (!form.value.name || form.value.name === strategyName(previousId)) {
    form.value.name = strategyName(newId);
  }
});

function close(action) {
  isOpen.value = false;
  if (action === 'confirm') {
    emit('confirm', {
      ...form.value,
      strategy_id: Number(form.value.strategy_id),
      name: form.value.name?.trim() || strategyName(form.value.strategy_id),
      initial_deposit: Number(form.value.initial_deposit) || 0,
      regular_deposit: Number(form.value.regular_deposit) || 0,
      duration: Number(form.value.duration) || 1,
    });
  } else {
    emit('cancel');
  }
}
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="close('cancel')" class="modal show d-block" style="z-index: 1060;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered" style="z-index: 1060">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle as="h3" class="modal-title">{{ isEdit ? t('calculationStrategyModal.title_edit') : t('calculationStrategyModal.title_add') }}</DialogTitle>
              <i class="btn-close cursor-pointer" @click="close('cancel')" :aria-label="t('calculationStrategyModal.cancel')"></i>
            </div>
            <form @submit.prevent="close('confirm')">
              <div class="modal-body">
                <div class="form-group mb-3">
                  <label for="calcStrategySelect" class="form-label">{{ t('calculationStrategyModal.strategy') }}</label>
                  <select id="calcStrategySelect" v-model.number="form.strategy_id" class="form-select" required>
                    <option v-for="strategy of strategies" :key="strategy.id" :value="strategy.id">{{ strategy.strategy_name ?? strategy.name }}</option>
                  </select>
                  <small v-if="selectedStrategy?.description" class="text-muted d-block mt-1">{{ selectedStrategy.description }}</small>
                </div>
                <div class="form-group mb-3">
                  <label for="calcStrategyName" class="form-label">{{ t('calculationStrategyModal.name') }}</label>
                  <input id="calcStrategyName" v-model="form.name" class="form-control" maxlength="100"/>
                </div>
                <template v-if="mode === 'SAVING'">
                  <div class="form-group mb-3">
                    <label class="form-label">{{ t('clientOnboardingOverlay.calculation_initial_deposit_amount') }}</label>
                    <CurrencyInput v-model="form.initial_deposit" :currency="currency" :min="limits?.initial_deposit_min" :max="limits?.initial_deposit_max"/>
                  </div>
                  <div class="form-group mb-3">
                    <label class="form-label">{{ t('clientOnboardingOverlay.calculation_regular_deposit_amount') }}</label>
                    <CurrencyInput v-model="form.regular_deposit" :currency="currency" :min="limits?.regular_deposit_min" :max="limits?.regular_deposit_max"/>
                  </div>
                  <div class="form-group mb-2">
                    <label class="form-label">{{ t('clientOnboardingOverlay.calculation_how_long_you_want_invest') }}</label>
                    <vue-slider v-model="form.duration" :interval="1" :min="3" :max="60" :hide-label="true" :marks="false"/>
                    <div class="vue-slider-result">
                      <span class="vue-slider-result-label">{{ t('clientOnboardingOverlay.calculation_investment_horizon_is') }}:</span>
                      <span class="vue-slider-result-value">{{ formatYearsString(form.duration) }}</span>
                    </div>
                  </div>
                </template>
                <div v-else class="alert alert-light small mb-0">
                  {{ targetInfo ?? t('calculationStrategyModal.target_info') }}
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-outline-tenant" @click="close('cancel')">{{ t('calculationStrategyModal.cancel') }}</button>
                <button type="submit" class="btn btn-primary" :disabled="!form.strategy_id">{{ isEdit ? t('calculationStrategyModal.save') : t('calculationStrategyModal.add') }}</button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
